import { CAPTURE_STEPS, type CapturedImage, type CaptureStepId } from '../types';

import classes from '../PassportPage.module.scss';

interface ReviewStepProps {
  images: Partial<Record<CaptureStepId, CapturedImage>>;
  onRetake: (stepId: CaptureStepId) => void;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting?: boolean;
  errorMessage?: string | null;
  primaryChecks?: Partial<Record<CaptureStepId, {
    status: 'checking' | 'passed' | 'failed';
    message?: string;
  }>>;
  stepsToShow?: CaptureStepId[];
  submitLabel?: string;
  title?: string;
  subtitle?: string;
}

const REQUIRED_CHECK_STEPS = new Set<CaptureStepId>(['spread', 'registration']);

export const ReviewStep = ({
  images,
  onRetake,
  onSubmit,
  onBack,
  isSubmitting = false,
  errorMessage = null,
  primaryChecks = {},
  stepsToShow,
  submitLabel = 'Отправить',
  title = 'Проверка фото',
  subtitle = 'Убедитесь, что все снимки четкие и данные читаются',
}: ReviewStepProps) => {
  const visibleSteps = stepsToShow?.length
    ? CAPTURE_STEPS.filter((step) => stepsToShow.includes(step.id))
    : CAPTURE_STEPS;
  const allCaptured = visibleSteps.every((step) => images[step.id]);
  const ocrSteps = visibleSteps
    .map((step) => step.id)
    .filter((stepId) => stepId !== 'selfie') as CaptureStepId[];
  const requiredChecksPassed = ocrSteps.every(
    (stepId) => primaryChecks[stepId]?.status === 'passed',
  );
  const isPrimaryCheckPending = ocrSteps.some(
    (stepId) => primaryChecks[stepId]?.status === 'checking',
  );

  return (
    <main className={classes.page}>
      <p className={classes.crumb}>Личный кабинет / Проверка документов</p>
      <h1 className={classes.title}>{title}</h1>
      <section className={classes.card}>
        <p className={classes.sideText}>{subtitle}</p>
        {visibleSteps.map((step) => {
          const image = images[step.id];
          const check = primaryChecks[step.id];
          return (
            <div key={step.id} className={classes.preview}>
              {image ? (
                <img src={image.dataUrl} alt={step.title} className={classes.previewImage} />
              ) : (
                <span className={classes.mark} aria-hidden>
                  +
                </span>
              )}
              <p className={classes.previewTitle}>{step.title}</p>
              {REQUIRED_CHECK_STEPS.has(step.id) ? (
                <p className={classes.previewText}>
                  {check?.status === 'checking' && 'Проверяем читаемость...'}
                  {check?.status === 'passed' && (check.message || 'Документ читается')}
                  {check?.status === 'failed' && (check.message || 'Документ не читается. Переснимите фото.')}
                  {!check && 'Нужна проверка читаемости'}
                </p>
              ) : null}
              <button type='button' className={classes.secondary} onClick={() => onRetake(step.id)}>
                Переснять
              </button>
            </div>
          );
        })}
        {errorMessage ? (
          <p className={classes.error} role='alert'>
            {errorMessage}
          </p>
        ) : null}
        <div className={classes.nav}>
          <button type='button' className={classes.secondary} onClick={onBack}>
            Назад
          </button>
          <button
            type='button'
            className={classes.primary}
            disabled={!allCaptured || !requiredChecksPassed || isPrimaryCheckPending || isSubmitting}
            onClick={onSubmit}
          >
            {isSubmitting ? 'Отправка...' : submitLabel}
          </button>
        </div>
      </section>
    </main>
  );
};
